/** Credit visual state for contact tiles (aligned with ContactsSnapshotCard / ContactManageModal). */
export type ContactCreditVisualStatus = 'passed' | 'declined' | 'pending' | 'none';

export type ContactCreditStatusOptions = {
    sunlightCreditStatus?: unknown;
    isSunlightPartnerExist?: boolean;
};

function asContactRecord(c: unknown): Record<string, unknown> | null {
    return c && typeof c === 'object' ? (c as Record<string, unknown>) : null;
}

function normalizeCreditStatusText(value: unknown): string {
    return String(value ?? '')
        .trim()
        .toLowerCase();
}

function isPassedCreditStatusText(status: string): boolean {
    return status === 'passed' || status === 'pass' || status === 'approved';
}

function isPendingCreditStatusText(status: string): boolean {
    if (!status) return false;
    return (
        status.includes('pend') ||
        status.includes('progress') ||
        status.includes('submitted') ||
        status.includes('in review') ||
        status.includes('processing') ||
        status === 'submitted'
    );
}

function isDeclinedCreditStatusText(status: string): boolean {
    if (!status) return false;
    return status.includes('declin') || status.includes('denied') || status === 'failed';
}

export function getFinancePartnerCreditStatusList(contact: Record<string, unknown>): Record<string, unknown>[] {
    const fpList = contact.financePartnerCreditStatusList as Record<string, unknown> | undefined;
    const raw = fpList?.CreditStatusList ?? fpList?.creditStatusList ?? [];
    if (!Array.isArray(raw)) return [];
    return raw.filter((row): row is Record<string, unknown> => !!row && typeof row === 'object');
}

function getFinancePartnerRowStatusText(row: Record<string, unknown>): string {
    return normalizeCreditStatusText(
        row.creditStatus ?? row.CreditStatus ?? row.Status ?? row.status ?? row.Application_Status,
    );
}

function isFinancePartnerRowExplicitlyDeclined(row: Record<string, unknown>): boolean {
    return isDeclinedCreditStatusText(getFinancePartnerRowStatusText(row));
}

function checkFinancePartnerCreditPassStatus(cust: Record<string, unknown>): boolean {
    const creditStatusList = getFinancePartnerCreditStatusList(cust);
    const syncContactId = cust.Finance_Partner_Sync_ContactId ?? cust.finance_Partner_Sync_ContactId;
    if (creditStatusList.length === 0 || !syncContactId) return false;

    const val: number[] = [];
    creditStatusList.forEach((row) => {
        const eleCheckStatus = row.creditCheckStatus ?? row.CreditCheckStatus ?? row.IsCreditPass ?? row.isCreditPass;
        if (eleCheckStatus === true && syncContactId) {
            const expireOn = row.expireOn ?? row.ExpireOn;
            if (expireOn) {
                const expiryDate = new Date(String(expireOn)).getTime();
                if (expiryDate < Date.now()) {
                    if (!val.includes(1)) val.push(1);
                } else if (!val.includes(2)) val.push(2);
            } else if (!val.includes(2)) val.push(2);
        } else if (!val.includes(3)) val.push(3);
    });
    return val.includes(2);
}

function getSunlightCreditCheckStatus(sunlightStatus: unknown): number {
    if (!sunlightStatus || typeof sunlightStatus !== 'object') return 5;
    const row = sunlightStatus as Record<string, unknown>;
    const expireOn = row.ExpireOn ?? row.expireOn;
    const creditStatus = row.CreditStatus ?? row.creditStatus;
    const creditStr = creditStatus == null ? '' : String(creditStatus);

    if (creditStatus && expireOn && !creditStr.includes('Decline')) {
        const expiryDate = new Date(String(expireOn)).getTime();
        if (expiryDate < Date.now() && creditStatus !== null) return 1;
        return 2;
    }
    if (creditStr === 'Declined' || creditStr === 'declined' || creditStr.includes('Decline')) return 4;
    if (creditStatus === null || creditStatus === undefined) return 5;
    return 3;
}

export function contactHasPassedCredit(
    c: unknown,
    isPrimary: boolean,
    options?: ContactCreditStatusOptions,
): boolean {
    const contact = asContactRecord(c);
    if (!contact) return false;

    const hasCreditCheckStatus = contact.CreditCheckStatus === true || contact.creditCheckStatus === true;

    const passStr = normalizeCreditStatusText(contact.CreditPassStatus ?? contact.creditPassStatus);
    const fpList = contact.financePartnerCreditStatusList as Record<string, unknown> | undefined;

    const isBaseCreditPass =
        checkFinancePartnerCreditPassStatus(contact) ||
        fpList?.IsAnyCreditStatusPass === true ||
        isPassedCreditStatusText(passStr);

    const isSunlightPass =
        !!options?.isSunlightPartnerExist &&
        isPrimary &&
        getSunlightCreditCheckStatus(options.sunlightCreditStatus) === 2;

    return !!((isBaseCreditPass || isSunlightPass) && hasCreditCheckStatus);
}

function contactCreditDeclined(
    c: unknown,
    isPrimary: boolean,
    options?: ContactCreditStatusOptions,
): boolean {
    const contact = asContactRecord(c);
    if (!contact || contactHasPassedCredit(c, isPrimary, options)) return false;

    const passStr = normalizeCreditStatusText(contact.CreditPassStatus ?? contact.creditPassStatus);
    if (isDeclinedCreditStatusText(passStr)) return true;

    const creditStatusList = getFinancePartnerCreditStatusList(contact);
    if (creditStatusList.some((row) => isFinancePartnerRowExplicitlyDeclined(row))) return true;

    if (options?.isSunlightPartnerExist && isPrimary && getSunlightCreditCheckStatus(options.sunlightCreditStatus) === 4) {
        return true;
    }
    return false;
}

function contactCreditPending(
    c: unknown,
    isPrimary: boolean,
    options?: ContactCreditStatusOptions,
): boolean {
    const contact = asContactRecord(c);
    if (!contact || contactHasPassedCredit(c, isPrimary, options) || contactCreditDeclined(c, isPrimary, options)) {
        return false;
    }

    const passStr = normalizeCreditStatusText(contact.CreditPassStatus ?? contact.creditPassStatus);
    if (isPendingCreditStatusText(passStr)) return true;

    const creditStatusList = getFinancePartnerCreditStatusList(contact);
    const syncContactId = contact.Finance_Partner_Sync_ContactId ?? contact.finance_Partner_Sync_ContactId;
    if (creditStatusList.length > 0 && syncContactId) {
        const hasInFlightPartnerCheck = creditStatusList.some((row) => isPendingCreditStatusText(getFinancePartnerRowStatusText(row)));
        if (hasInFlightPartnerCheck) return true;
    }

    if (options?.isSunlightPartnerExist && isPrimary) {
        const code = getSunlightCreditCheckStatus(options.sunlightCreditStatus);
        if (code === 3) return true;
    }

    return false;
}

export function resolveContactCreditVisualStatus(
    c: unknown,
    isPrimary: boolean,
    options?: ContactCreditStatusOptions,
): ContactCreditVisualStatus {
    if (!c) return 'none';
    if (contactHasPassedCredit(c, isPrimary, options)) return 'passed';
    if (contactCreditPending(c, isPrimary, options)) return 'pending';
    if (contactCreditDeclined(c, isPrimary, options)) return 'declined';
    return 'none';
}

export function getPrimaryContactRecord(details: unknown): Record<string, unknown> | null {
    if (!details || typeof details !== 'object') return null;
    const d = details as Record<string, unknown>;
    const customers = d.Customers;
    if (!Array.isArray(customers) || customers.length === 0) return null;
    const primary = customers.find(
        (row) =>
            row &&
            typeof row === 'object' &&
            ((row as Record<string, unknown>).IsPrimaryContact || (row as Record<string, unknown>).isPrimaryContact),
    );
    const record = (primary ?? customers[0]) as Record<string, unknown>;
    return record ?? null;
}

export function opportunityHasSunlightPartner(details: unknown): boolean {
    if (!details || typeof details !== 'object') return false;
    const d = details as Record<string, unknown>;
    const products = (d.OpportunityProduct ?? d.opportunityProduct ?? []) as unknown[];
    return products.some((p) => {
        if (!p || typeof p !== 'object') return false;
        const row = p as Record<string, unknown>;
        return row.Finance_Partner === 'Sunlight' || row.Finance_Partner_Display_Name === 'Sunlight';
    });
}

export function getCreditRowStatus(row: any, hasSyncContactId: boolean, primaryFinancePartner: string): number {
    // Finance partner names can sometimes come in with or without spaces
    const isGoodleapOrEnfin = primaryFinancePartner === 'Goodleap' || primaryFinancePartner === 'Good Leap' || primaryFinancePartner === 'Enfin';
    
    const isCreditCheckPass = row.creditCheckStatus === true || row.CreditCheckStatus === true;

    if (isCreditCheckPass && (hasSyncContactId || isGoodleapOrEnfin)) {
        const date = Date.now();
        const expireOn = row.expireOn || row.ExpireOn;
        const expiryDate = expireOn ? new Date(expireOn).getTime() : 0;
        
        if (expiryDate < date && (row.creditCheckStatus != null || row.CreditCheckStatus != null)) {
            return 1; // Expired
        } else {
            return 2; // Passed
        }
    } else {
        const statusStr = row.Finance_Partner_Sync_Credit_Status;
        if (statusStr === 'Declined' || statusStr === 'declined') return 4;
        if (statusStr === null || statusStr === undefined) return 5;
        return 3; // Pending
    }
}
